import React, { useState } from "react";
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import LogIcon from "@mui/icons-material/Grading";
import ProjectIcon from "@mui/icons-material/FolderCopy";

import AllUsers from "./AllUsers";
import StatisticsDashboard from "./StatisticsDashboard";
import ProjectsOverviewPage from "../projectsOverview/ProjectsOverviewPage";

const drawerWidth = 240;

type PageKey = "statistics" | "users" | "projects" | "logs";

const AdminCenterPage: React.FC = () => {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [active, setActive] = useState<PageKey>("statistics");

  const navItems: { key: PageKey; label: string; icon: React.ReactNode }[] = [
    { key: "statistics", label: "Dashboard", icon: <DashboardIcon /> },
    { key: "users", label: "Users", icon: <PeopleIcon /> },
    { key: "logs", label: "Logs", icon: <LogIcon /> },
    { key: "projects", label: "Projects", icon: <ProjectIcon /> },
  ];

  const handleToggle = () => setMobileOpen((v) => !v);

  const DrawerContent = (
    <Box
      role="presentation"
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Toolbar>
        <Typography variant="h6" noWrap>
          Admin Center
        </Typography>
      </Toolbar>
      <List sx={{ flex: 1 }}>
        {navItems.map((item) => (
          <ListItem key={item.key} disablePadding>
            <ListItemButton
              selected={active === item.key}
              onClick={() => {
                setActive(item.key);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const renderContent = () => {
    switch (active) {
      case "users":
        return <AllUsers />;
      case "statistics":
        return <StatisticsDashboard />;
      case "logs":
        return null;
      case "projects":
        return <ProjectsOverviewPage view="admin" />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar position="fixed" color="primary">
        <Toolbar sx={{ top: "4rem" }}>
          {!mdUp && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            {active === "users"
              ? "Users"
              : active === "statistics"
              ? "Statistics"
              : active === "logs"
              ? "Logs"
              : active === "projects"
              ? "Projects"
              : null}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="admin navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              top: "4rem",
              height: "calc(100% - 4rem)",
            },
          }}
        >
          {DrawerContent}
        </Drawer>

        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              top: "4rem",
              height: "calc(100% - 4rem)",
            },
          }}
        >
          {DrawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          p: 0,
          height: "100dvh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Toolbar />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminCenterPage;
